/**
 * Structured AI Employee role catalog + industry validation for SiteGlue Trish onboarding.
 * Recommendations must be selected from this catalog (or validated against it).
 */

import type { IndustryVertical } from "~/lib/utils/industry-profile";
import type { RecommendedAiEmployee } from "~/lib/siteglue/consultant-onboarding";

export type AiEmployeeRoleDefinition = {
  id: string;
  displayName: string;
  department: string;
  supportedIndustries: Array<IndustryVertical | "all">;
  excludedIndustries: IndustryVertical[];
  responsibilities: string[];
  recommendedChannels: string[];
  schedulingCapabilities: string[];
  integrationRequirements: string[];
  handoffExamples: string[];
  takeoverExamples: string[];
  keywords: string[];
  priorityRules: Array<"launch_first" | "high_value" | "expand_next" | "optional">;
};

const HEALTHCARE_INDUSTRIES: IndustryVertical[] = ["healthcare"];
const NON_HEALTHCARE: IndustryVertical[] = [
  "education",
  "legal",
  "retail",
  "home_services",
  "automotive",
  "financial",
  "real_estate",
  "hospitality",
  "fitness",
  "manufacturing",
  "consulting",
  "nonprofit",
  "general",
];

export const AI_EMPLOYEE_ROLE_CATALOG: AiEmployeeRoleDefinition[] = [
  {
    id: "new-student-advisor",
    displayName: "New Student Advisor",
    department: "Admissions and Enrollment",
    supportedIndustries: ["education"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: [
      "Answer admissions questions",
      "Support program discovery",
      "Capture prospective students",
      "Schedule admissions consultations",
      "Route complex questions to admissions staff",
    ],
    recommendedChannels: ["Website Chat", "Phone", "SMS", "Email"],
    schedulingCapabilities: ["Admissions consultations", "Information sessions"],
    integrationRequirements: ["Google Calendar", "Calendly", "Zoom"],
    handoffExamples: ["Financial aid questions", "Application status exceptions"],
    takeoverExamples: ["Complex enrollment appeals", "Compliance-sensitive cases"],
    keywords: ["admissions", "enrollment", "prospective", "program", "tuition"],
    priorityRules: ["launch_first"],
  },
  {
    id: "enrollment-advisor",
    displayName: "Enrollment Advisor",
    department: "Enrollment",
    supportedIndustries: ["education"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: [
      "Support enrollment questions",
      "Capture callback requests",
      "Schedule enrollment consultations",
      "Follow up on incomplete applications",
    ],
    recommendedChannels: ["Website Chat", "Phone", "SMS", "Email"],
    schedulingCapabilities: ["Enrollment callbacks", "Advisor appointments"],
    integrationRequirements: ["Google Calendar", "Calendly"],
    handoffExamples: ["Registrar issues", "Transcript evaluation"],
    takeoverExamples: ["Account-specific enrollment holds"],
    keywords: ["enrollment", "application", "advisor"],
    priorityRules: ["high_value"],
  },
  {
    id: "online-support-specialist",
    displayName: "Online Support Specialist",
    department: "Student Support",
    supportedIndustries: ["education", "retail", "general", "consulting", "manufacturing"],
    excludedIndustries: [],
    responsibilities: [
      "Answer routine questions from approved knowledge",
      "Capture inquiries",
      "Route account-specific issues to staff",
    ],
    recommendedChannels: ["Website Chat", "Email", "SMS"],
    schedulingCapabilities: [],
    integrationRequirements: [],
    handoffExamples: ["Account access", "Billing disputes"],
    takeoverExamples: ["Escalated complaints"],
    keywords: ["support", "faq", "after-hours"],
    priorityRules: ["expand_next"],
  },
  {
    id: "scheduling-specialist",
    displayName: "Scheduling Specialist",
    department: "Scheduling",
    supportedIndustries: ["all"],
    excludedIndustries: [],
    responsibilities: [
      "Coordinate consultations and appointments",
      "Confirm preferred times",
      "Send reminders when configured",
    ],
    recommendedChannels: ["Website Chat", "Phone", "SMS"],
    schedulingCapabilities: ["Consultations", "Tours", "Demos"],
    integrationRequirements: ["Google Calendar", "Calendly", "Zoom"],
    handoffExamples: ["Conflicts needing a person"],
    takeoverExamples: ["VIP or exception scheduling"],
    keywords: ["schedule", "appointment", "consultation", "tour"],
    priorityRules: ["optional"],
  },
  {
    id: "admissions-advisor",
    displayName: "Admissions Advisor",
    department: "Admissions",
    supportedIndustries: ["education"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: [
      "Tours and enrollment appointments",
      "Answer admissions FAQs",
      "Capture after-hours enrollment interest",
    ],
    recommendedChannels: ["Website Chat", "Phone", "Email"],
    schedulingCapabilities: ["Campus tours", "Virtual information sessions"],
    integrationRequirements: ["Calendly", "Zoom"],
    handoffExamples: ["Financial aid"],
    takeoverExamples: ["Special admissions cases"],
    keywords: ["admissions", "tour", "campus"],
    priorityRules: ["high_value"],
  },
  {
    id: "patient-coordinator",
    displayName: "Patient Coordinator",
    department: "Front Desk",
    supportedIndustries: HEALTHCARE_INDUSTRIES,
    excludedIndustries: NON_HEALTHCARE,
    responsibilities: [
      "Clinical appointment coordination",
      "New and existing patient scheduling",
      "Intake and handoff to care teams",
    ],
    recommendedChannels: ["Website Chat", "Phone", "SMS"],
    schedulingCapabilities: ["Book", "Reschedule", "Cancel"],
    integrationRequirements: ["Juvonno", "NexHealth"],
    handoffExamples: ["Clinical questions", "Insurance verification"],
    takeoverExamples: ["Urgent clinical triage"],
    keywords: ["patient", "clinic", "clinical", "medical", "dental"],
    priorityRules: ["launch_first"],
  },
  {
    id: "online-patient-coordinator",
    displayName: "Online Patient Coordinator",
    department: "Front Desk",
    supportedIndustries: HEALTHCARE_INDUSTRIES,
    excludedIndustries: NON_HEALTHCARE,
    responsibilities: [
      "New and existing dental or clinical scheduling",
      "Faster new-patient intake",
    ],
    recommendedChannels: ["Website Chat", "Phone", "SMS"],
    schedulingCapabilities: ["Book", "Reschedule", "Cancel"],
    integrationRequirements: ["NexHealth", "Juvonno"],
    handoffExamples: ["Clinical escalation"],
    takeoverExamples: ["Chairside coordination"],
    keywords: ["patient", "dental", "clinical"],
    priorityRules: ["launch_first"],
  },
  {
    id: "front-desk-receptionist",
    displayName: "Online Support Agent",
    department: "Front Desk",
    supportedIndustries: ["all"],
    excludedIndustries: [],
    responsibilities: [
      "Answer website Live Chat questions",
      "Capture inquiries and contact details",
      "Share approved knowledge",
      "Route complex requests via Team Handoff™",
    ],
    recommendedChannels: ["Website Chat"],
    schedulingCapabilities: [],
    integrationRequirements: [],
    handoffExamples: ["Complex requests"],
    takeoverExamples: ["Team Takeover™ when a person should continue"],
    keywords: ["reception", "front desk", "greeting", "live chat", "online support"],
    priorityRules: ["launch_first", "optional"],
  },
  {
    id: "online-patient-coordinator",
    displayName: "Online Patient Coordinator",
    department: "Patient Services",
    supportedIndustries: HEALTHCARE_INDUSTRIES,
    excludedIndustries: [],
    responsibilities: [
      "Answer website Live Chat questions for patients",
      "Capture new-patient inquiries",
      "Share hours, locations, and services from approved knowledge",
      "Hand off clinical or urgent requests to the team",
    ],
    recommendedChannels: ["Website Chat"],
    schedulingCapabilities: ["Appointment request capture"],
    integrationRequirements: [],
    handoffExamples: ["Clinical questions", "Urgent symptoms"],
    takeoverExamples: ["Staff continues sensitive conversations"],
    keywords: ["patient", "coordinator", "live chat", "new patient"],
    priorityRules: ["launch_first"],
  },
  {
    id: "live-chat-support-agent",
    displayName: "Live Chat Support Agent",
    department: "Support",
    supportedIndustries: ["all"],
    excludedIndustries: [],
    responsibilities: [
      "Answer Website Chat questions",
      "Capture leads and contact details",
      "Support after-hours inquiries from knowledge",
      "Escalate with Team Handoff™",
    ],
    recommendedChannels: ["Website Chat"],
    schedulingCapabilities: [],
    integrationRequirements: [],
    handoffExamples: ["Escalations outside policy"],
    takeoverExamples: ["Team Takeover™ for complex deals"],
    keywords: ["live chat", "support agent", "website chat"],
    priorityRules: ["launch_first", "optional"],
  },
  {
    id: "digital-welcome-coordinator",
    displayName: "Digital Welcome Coordinator",
    department: "Welcome Ministry",
    supportedIndustries: ["nonprofit"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: [
      "Welcome first-time visitors and help them plan a Sunday visit",
      "Share service times, ministries, and event information",
      "Capture prayer or care requests",
      "Route messages to the appropriate ministry or care team",
    ],
    recommendedChannels: ["Website Chat", "Phone", "Email"],
    schedulingCapabilities: ["Pastoral appointments", "Counseling scheduling"],
    integrationRequirements: ["Google Calendar", "Calendly"],
    handoffExamples: ["Pastoral emergencies", "Confidential counseling"],
    takeoverExamples: ["Sensitive pastoral care"],
    keywords: [
      "parish",
      "church",
      "ministry",
      "welcome",
      "visitor",
      "digital welcome",
      "hospitality",
    ],
    priorityRules: ["launch_first"],
  },
  {
    id: "community-outreach-assistant",
    displayName: "Community Outreach Assistant",
    department: "Outreach",
    supportedIndustries: ["nonprofit"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: [
      "Support food pantry and outreach inquiries",
      "Collect volunteer interest",
      "Share giving and donation information",
      "Improve follow-up with new visitors",
    ],
    recommendedChannels: ["Website Chat", "Email", "SMS"],
    schedulingCapabilities: [],
    integrationRequirements: [],
    handoffExamples: ["Urgent assistance requests"],
    takeoverExamples: ["Crisis support"],
    keywords: ["outreach", "volunteer", "pantry", "donation", "community"],
    priorityRules: ["high_value"],
  },
  {
    id: "consultation-coordinator",
    displayName: "Consultation Coordinator",
    department: "Intake",
    supportedIndustries: ["legal", "financial", "consulting"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: [
      "Legal or professional consultation intake",
      "Qualify and route to the right specialist",
    ],
    recommendedChannels: ["Website Chat", "Phone", "Email"],
    schedulingCapabilities: ["Consultations"],
    integrationRequirements: ["Calendly", "Google Calendar"],
    handoffExamples: ["Conflict checks"],
    takeoverExamples: ["Retainer discussions"],
    keywords: ["consultation", "attorney", "legal"],
    priorityRules: ["launch_first"],
  },
  {
    id: "service-dispatcher",
    displayName: "Service Dispatcher",
    department: "Dispatch",
    supportedIndustries: ["home_services", "automotive"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: ["Service window and job scheduling", "Dispatch requests"],
    recommendedChannels: ["Website Chat", "Phone", "SMS"],
    schedulingCapabilities: ["Service windows"],
    integrationRequirements: ["Google Calendar"],
    handoffExamples: ["Emergency service"],
    takeoverExamples: ["On-site technician coordination"],
    keywords: ["dispatch", "hvac", "plumbing", "service"],
    priorityRules: ["launch_first"],
  },
  {
    id: "demo-scheduling-assistant",
    displayName: "Demo Scheduling Assistant",
    department: "Sales",
    supportedIndustries: ["consulting", "manufacturing", "general"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: ["Product demo bookings", "Qualification before booking"],
    recommendedChannels: ["Website Chat", "Email", "Phone"],
    schedulingCapabilities: ["Demo scheduling"],
    integrationRequirements: ["Calendly", "Google Calendar", "Zoom"],
    handoffExamples: ["Enterprise deals"],
    takeoverExamples: ["Custom demos"],
    keywords: ["demo", "saas", "software"],
    priorityRules: ["launch_first"],
  },
  {
    id: "showing-coordinator",
    displayName: "Showing Coordinator",
    department: "Sales",
    supportedIndustries: ["real_estate"],
    excludedIndustries: HEALTHCARE_INDUSTRIES,
    responsibilities: ["Property showing requests", "Qualified showings"],
    recommendedChannels: ["Website Chat", "Phone", "SMS"],
    schedulingCapabilities: ["Showings"],
    integrationRequirements: ["Google Calendar", "Calendly"],
    handoffExamples: ["Offer negotiations"],
    takeoverExamples: ["Agent-led showings"],
    keywords: ["showing", "property", "listing"],
    priorityRules: ["launch_first"],
  },
];

/** Roles / phrases that must never appear for non-healthcare industries. */
export const HEALTHCARE_ONLY_ROLE_PATTERNS = [
  /patient\s+coordinator/i,
  /online\s+patient/i,
  /medical\s+receptionist/i,
  /insurance\s+verification/i,
  /clinical\s+intake/i,
  /new\s+patient/i,
  /dental\s+patient/i,
];

export const HEALTHCARE_ONLY_INTEGRATIONS = [
  /juvonno/i,
  /nexhealth/i,
  /\bemr\b/i,
  /\bpms\b/i,
];

export function normalizeIndustryKey(
  verticalOrLabel?: string | null,
): IndustryVertical | "other" {
  const v = (verticalOrLabel ?? "").toLowerCase().trim();
  if (!v) return "other";
  // Faith / nonprofit before education — parish sites often mention school/campus ministry.
  if (
    v === "nonprofit" ||
    v.includes("church") ||
    v.includes("parish") ||
    v.includes("catholic") ||
    v.includes("ministry") ||
    v.includes("diocese") ||
    v.includes("congregation") ||
    v.includes("synagogue") ||
    v.includes("mosque") ||
    v.includes("nonprofit") ||
    v.includes("non-profit") ||
    v.includes("charity") ||
    v.includes("food pantry")
  ) {
    return "nonprofit";
  }
  if (
    v === "education" ||
    v.includes("educat") ||
    v.includes("univers") ||
    v.includes("college") ||
    (v.includes("school") && !v.includes("parish"))
  ) {
    return "education";
  }
  if (v === "healthcare" || v.includes("health") || v.includes("dental") || v.includes("clinic")) {
    // "healthcare" label and dental practice — but not "health sciences" education (handled above)
    if (v.includes("educat") || v.includes("univers") || v.includes("college")) return "education";
    return "healthcare";
  }
  if (v === "legal" || v.includes("law")) return "legal";
  if (v === "home_services" || v.includes("home")) return "home_services";
  if (v === "real_estate" || v.includes("real")) return "real_estate";
  if (v === "automotive" || v.includes("auto")) return "automotive";
  if (v === "financial" || v.includes("financ")) return "financial";
  if (v === "retail" || v.includes("retail") || v.includes("e-?commerce")) return "retail";
  if (v === "hospitality" || v.includes("restaurant") || v.includes("hotel")) return "hospitality";
  if (v === "fitness" || v.includes("gym")) return "fitness";
  if (v === "manufacturing" || v.includes("manufactur")) return "manufacturing";
  if (v === "consulting" || v.includes("consult") || v.includes("saas") || v.includes("software")) {
    return "consulting";
  }
  if (v === "general") return "general";
  return "other";
}

export function isRoleAllowedForIndustry(
  role: AiEmployeeRoleDefinition,
  industry: IndustryVertical | "other",
): boolean {
  if (industry !== "other" && role.excludedIndustries.includes(industry)) return false;
  if (role.supportedIndustries.includes("all")) return true;
  if (industry === "other") {
    return !role.supportedIndustries.every((s) => s === "healthcare");
  }
  return role.supportedIndustries.includes(industry);
}

export type RoleValidationResult = {
  ok: boolean;
  roleName: string;
  rejectionReason?: string;
};

export function validateRecommendedRoleName(
  roleName: string,
  industryRaw?: string | null,
): RoleValidationResult {
  const industry = normalizeIndustryKey(industryRaw);
  const catalogMatch = AI_EMPLOYEE_ROLE_CATALOG.find(
    (r) => r.displayName.toLowerCase() === roleName.toLowerCase(),
  );

  if (catalogMatch && !isRoleAllowedForIndustry(catalogMatch, industry)) {
    return {
      ok: false,
      roleName,
      rejectionReason: `Role "${roleName}" is not allowed for industry "${industry}"`,
    };
  }

  if (industry !== "healthcare") {
    for (const pattern of HEALTHCARE_ONLY_ROLE_PATTERNS) {
      if (pattern.test(roleName)) {
        return {
          ok: false,
          roleName,
          rejectionReason: `Healthcare-only role blocked for industry "${industry}"`,
        };
      }
    }
  }

  if (industry === "education") {
    if (/clinical|patient|insurance verification|emr|pms|juvonno|nexhealth/i.test(roleName)) {
      return {
        ok: false,
        roleName,
        rejectionReason: "Education org cannot receive clinical/patient roles",
      };
    }
  }

  return { ok: true, roleName };
}

export function validateIntegrationForIndustry(
  integration: string,
  industryRaw?: string | null,
): RoleValidationResult {
  const industry = normalizeIndustryKey(industryRaw);
  if (industry === "healthcare") return { ok: true, roleName: integration };

  for (const pattern of HEALTHCARE_ONLY_INTEGRATIONS) {
    if (pattern.test(integration)) {
      return {
        ok: false,
        roleName: integration,
        rejectionReason: `Healthcare integration "${integration}" blocked for industry "${industry}"`,
      };
    }
  }
  return { ok: true, roleName: integration };
}

export function filterValidRecommendations(
  roles: RecommendedAiEmployee[],
  industryRaw?: string | null,
): { accepted: RecommendedAiEmployee[]; rejected: RoleValidationResult[] } {
  const accepted: RecommendedAiEmployee[] = [];
  const rejected: RoleValidationResult[] = [];

  for (const role of roles) {
    const result = validateRecommendedRoleName(role.roleName, industryRaw);
    if (result.ok) {
      if (role.requiredIntegration) {
        const integ = validateIntegrationForIndustry(role.requiredIntegration, industryRaw);
        if (!integ.ok) {
          rejected.push(integ);
          accepted.push({ ...role, requiredIntegration: undefined });
          continue;
        }
      }
      accepted.push(role);
    } else {
      rejected.push(result);
      if (typeof console !== "undefined") {
        console.info("[onboarding] recommendation_rejected", {
          industry: normalizeIndustryKey(industryRaw),
          roleCandidate: role.roleName,
          validationResult: "rejected",
          rejectionReason: result.rejectionReason,
        });
      }
    }
  }

  return { accepted, rejected };
}

export function catalogRolesForIndustry(
  industryRaw?: string | null,
): AiEmployeeRoleDefinition[] {
  const industry = normalizeIndustryKey(industryRaw);
  return AI_EMPLOYEE_ROLE_CATALOG.filter((r) => isRoleAllowedForIndustry(r, industry));
}

export function safeFallbackRolesForIndustry(
  industryRaw?: string | null,
): RecommendedAiEmployee[] {
  const industry = normalizeIndustryKey(industryRaw);
  const make = (
    roleName: string,
    businessNeed: string,
    benefit: string,
  ): RecommendedAiEmployee => ({
    roleName,
    businessNeed,
    channels: ["Website Chat"],
    schedulingResponsibility: "Coordinate when selected",
    humanHandoff: "Team Handoff™ when outside policy or customer requests a person",
    teamTakeover: true,
    expectedBenefit: benefit,
    priority: "recommended_first",
  });

  // Intern first-hire: ONE role, Website Chat only — sell outcomes, not chatbot tasks.
  if (industry === "education") {
    return [
      make(
        "New Student Advisor",
        "Help your school convert website visitors into admissions inquiries and campus connections.",
        "More admissions inquiries with Team Handoff™ to advisors",
      ),
    ];
  }

  if (industry === "nonprofit") {
    return [
      make(
        "Digital Welcome Coordinator",
        "Help first-time visitors plan a visit and connect people with the right ministry, events, and care team.",
        "More first-time visitor engagement and ministry connections with Team Handoff™",
      ),
    ];
  }

  if (industry === "healthcare") {
    return [
      make(
        "Online Patient Coordinator",
        "Help your practice turn website visitors into new-patient appointments and care requests.",
        "More new patient appointments with Always a Human-in-the-Loop™",
      ),
    ];
  }

  if (industry === "legal") {
    return [
      make(
        "Client Intake Coordinator",
        "Help your firm turn website visitors into consultation requests for the right practice area.",
        "More consultation requests with Team Handoff™ to attorneys",
      ),
    ];
  }

  return [
    make(
      "Live Chat Support Agent",
      "Help your organization turn website visitors into qualified inquiries and next steps for your team.",
      "More qualified inquiries on Intern Website Chat with Team Handoff™",
    ),
  ];
}

/** Map legacy / in-person titles to website Live Chat roles (Intern plan). */
export function remapRoleNameForWebsiteChat(
  roleName: string,
  industryRaw?: string | null,
): string {
  const industry = normalizeIndustryKey(industryRaw);
  const raw = roleName.trim();
  if (!raw) {
    if (industry === "healthcare") return "Online Patient Coordinator";
    if (industry === "nonprofit") return "Digital Welcome Coordinator";
    return "Live Chat Support Agent";
  }
  // Legacy church title → current Intern Website Chat role.
  if (/parish welcome coordinator/i.test(raw)) {
    return "Digital Welcome Coordinator";
  }
  // Keep faith / nonprofit titles as-is (including Digital Welcome Coordinator).
  if (/digital welcome|ministry|welcome coordinator|outreach|community/i.test(raw)) {
    return raw;
  }
  if (/front\s*desk|receptionist|office\s*manager/i.test(raw)) {
    if (industry === "healthcare") return "Online Patient Coordinator";
    if (industry === "nonprofit") return "Digital Welcome Coordinator";
    return "Live Chat Support Agent";
  }
  if (/^patient coordinator$/i.test(raw)) {
    return "Online Patient Coordinator";
  }
  return raw;
}

/** Default SiteGlue AI Employee first name + avatar when none is chosen yet. */
export const DEFAULT_AI_EMPLOYEE_NAME = "Samantha";
export const DEFAULT_AI_EMPLOYEE_AVATAR = "/images/profiles/Samantha.webp";

/** Default preset avatar URL for a first name (e.g. Samantha → /images/profiles/Samantha.webp). */
export function defaultAvatarUrlForAgentName(agentName: string): string {
  const name = agentName.trim().toLowerCase();
  if (!name) return DEFAULT_AI_EMPLOYEE_AVATAR;
  // Lazy import avoided — keep in sync with AGENT_PROFILE_PRESETS filenames.
  const known: Record<string, string> = {
    mia: "/images/profiles/Mia.png",
    victoria: "/images/profiles/Victoria.gif",
    annie: "/images/profiles/Annie.png",
    samantha: DEFAULT_AI_EMPLOYEE_AVATAR,
    sunday: "/images/profiles/Mary.webp", // legacy church name → Grace avatar set
    grace: "/images/profiles/Mary.webp",
    christie: "/images/profiles/Christie.png",
    lisa: "/images/profiles/lisa.png",
    rebecca: "/images/profiles/Rebecca.png",
    sam: "/images/profiles/Sam.png",
    denise: "/images/profiles/Denise.webp",
    mary: "/images/profiles/Mary.webp",
    dua: "/images/profiles/dua.webp",
    andrew: "/images/profiles/Andrew.webp",
    ben: "/images/profiles/Ben.webp",
    rob: "/images/profiles/Rob.webp",
    evelyn: "/images/profiles/Evelyn.png",
    william: "/images/profiles/William.png",
    leo: "/images/profiles/Leo.png",
    astrid: "/images/profiles/Astrid.png",
    janathan: "/images/profiles/Janathan.png",
    katie: "/images/profiles/Katie.png",
  };
  if (known[name]) return known[name]!;
  // Male-leaning names without a preset → William; otherwise Samantha.
  if (/^(john|james|michael|david|robert|william|thomas|mark|paul|chris|brian|kevin|jason|ryan|matt|matthew|daniel|dan|steve|steven|joe|joseph|tim|timothy|jeff|jeffrey|scott|eric|greg|gregory|jerry|tom|tommy|jim|jimmy)$/i.test(name)) {
    return "/images/profiles/William.png";
  }
  return DEFAULT_AI_EMPLOYEE_AVATAR;
}
